import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import Layout from '../../components/Layout'
import { sendBroadcast, previewSegment } from '../../api/broadcast'
import { uploadProductImage } from '../../api/products'
import { useAuth } from '../../context/AuthContext'

const API = import.meta.env.VITE_API_URL ?? ''

const SEGMENTS = [
  { value: 'all',          label: 'Все клиенты',              desc: 'Все верифицированные активные клиенты' },
  { value: 'overdue',      label: 'Есть просрочка',           desc: 'Клиенты с хотя бы одним просроченным платежом' },
  { value: 'active_orders',label: 'Активная рассрочка',       desc: 'Клиенты с активными заявками (сейчас платят)' },
  { value: 'no_orders',    label: 'Нет заявок',               desc: 'Верифицированы, но ещё не оформили ни одной рассрочки' },
  { value: 'closed_loyal', label: 'Лояльные (закрыли рассрочку)', desc: 'Клиенты, полностью погасившие хотя бы одну рассрочку' },
  { value: 'amount_range', label: 'По сумме заявок ($)',      desc: 'Клиенты с общей суммой заявок в заданном диапазоне' },
]

const SEND_METHODS = [
  { bot: true,  miniapp: true,  label: 'Бот + MiniApp' },
  { bot: true,  miniapp: false, label: 'Только бот' },
  { bot: false, miniapp: true,  label: 'Только MiniApp' },
]

export default function BroadcastPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const lang = user?.language ?? 'ru'

  const [title, setTitle]       = useState('')
  const [body, setBody]         = useState('')
  const [imagePath, setImage]   = useState('')
  const [segment, setSegment]   = useState('all')
  const [amtMin, setAmtMin]     = useState('')
  const [amtMax, setAmtMax]     = useState('')
  const [sendIdx, setSendIdx]   = useState(0)
  const [previewCnt, setPreview]= useState<number | null>(null)
  const [uploading, setUploading] = useState(false)

  const segmentParams = segment === 'amount_range'
    ? { min: parseFloat(amtMin) || 0, max: parseFloat(amtMax) || 999999 }
    : undefined

  const previewMut = useMutation({
    mutationFn: () => previewSegment({ segment, segment_params: segmentParams }),
    onSuccess: (d) => setPreview(d.count),
    onError: () => toast.error('Ошибка при подсчёте'),
  })

  const sendMut = useMutation({
    mutationFn: () => sendBroadcast({
      title, body,
      image_path: imagePath || undefined,
      segment,
      segment_params: segmentParams,
      send_bot: SEND_METHODS[sendIdx].bot,
      send_miniapp: SEND_METHODS[sendIdx].miniapp,
    }),
    onSuccess: (ann) => {
      toast.success(`Отправлено ${ann.recipient_count} клиентам`)
      navigate('/admin/more')
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail ?? 'Ошибка'),
  })

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const res = await uploadProductImage(file)
      setImage(res.image_path)
      toast.success('Фото загружено')
    } catch { toast.error('Ошибка загрузки') }
    finally { setUploading(false); e.target.value = '' }
  }

  const canSend = title.trim() && body.trim()

  return (
    <Layout title="Рассылка" hideNav>
      <button
        onClick={() => navigate(-1)}
        style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#A8A8AE', marginBottom: 20, fontSize: 13, background: 'none', border: 'none', padding: 0 }}
      >
        <BackIcon /> Назад
      </button>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* Title */}
        <div>
          <p style={labelStyle}>Заголовок</p>
          <input
            className="input mt-1"
            placeholder="Акция, скидки, важное..."
            value={title}
            onChange={e => setTitle(e.target.value)}
            maxLength={200}
          />
        </div>

        {/* Body */}
        <div>
          <p style={labelStyle}>Текст сообщения</p>
          <textarea
            className="input mt-1 resize-none"
            rows={4}
            placeholder="Текст объявления..."
            value={body}
            onChange={e => setBody(e.target.value)}
          />
        </div>

        {/* Photo */}
        <div>
          <p style={labelStyle}>Фото (опционально)</p>
          {imagePath ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
              <img src={`${API}/uploads/${imagePath}`} alt="" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 10 }} />
              <button onClick={() => setImage('')} style={{ fontSize: 12, color: '#D97757', background: 'none', border: 'none', cursor: 'pointer' }}>
                Удалить
              </button>
            </div>
          ) : (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 8, padding: '8px 14px', borderRadius: 10, border: '1.5px dashed rgba(255,255,255,0.15)', cursor: 'pointer', fontSize: 13, color: '#8A8A96' }}>
              📷 Загрузить фото
              <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} disabled={uploading} />
            </label>
          )}
        </div>

        {/* Segment */}
        <div>
          <p style={labelStyle}>Сегмент получателей</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
            {SEGMENTS.map(s => (
              <button
                key={s.value}
                onClick={() => { setSegment(s.value); setPreview(null) }}
                style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                  padding: '10px 14px', borderRadius: 12, textAlign: 'left',
                  background: segment === s.value ? 'rgba(249,115,22,0.08)' : '#131316',
                  border: segment === s.value ? '1px solid rgba(249,115,22,0.30)' : '1px solid rgba(255,255,255,0.06)',
                  cursor: 'pointer',
                }}
              >
                <span style={{ fontSize: 13, color: segment === s.value ? '#F97316' : '#F5F4F0', fontWeight: 500 }}>{s.label}</span>
                <span style={{ fontSize: 11, color: '#8A8A96', marginTop: 2 }}>{s.desc}</span>
              </button>
            ))}
          </div>

          {segment === 'amount_range' && (
            <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 11, color: '#8A8A96', marginBottom: 4 }}>От ($)</p>
                <input className="input" type="number" placeholder="0" value={amtMin} onChange={e => { setAmtMin(e.target.value); setPreview(null) }} />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: 11, color: '#8A8A96', marginBottom: 4 }}>До ($)</p>
                <input className="input" type="number" placeholder="без ограничений" value={amtMax} onChange={e => { setAmtMax(e.target.value); setPreview(null) }} />
              </div>
            </div>
          )}
        </div>

        {/* Preview count */}
        <button
          onClick={() => previewMut.mutate()}
          disabled={previewMut.isPending}
          style={{ padding: '10px 16px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.10)', background: 'transparent', color: '#A8A8AE', fontSize: 13, cursor: 'pointer' }}
        >
          {previewMut.isPending ? 'Считаем...' : previewCnt === null ? '👁 Сколько получат?' : `👥 Получат: ${previewCnt} клиентов`}
        </button>

        {/* Send method */}
        <div>
          <p style={labelStyle}>Способ отправки</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            {SEND_METHODS.map((m, i) => (
              <button
                key={i}
                onClick={() => setSendIdx(i)}
                style={{
                  flex: 1, padding: '10px 8px', borderRadius: 10, fontSize: 12,
                  background: sendIdx === i ? 'rgba(249,115,22,0.08)' : '#131316',
                  border: sendIdx === i ? '1px solid rgba(249,115,22,0.30)' : '1px solid rgba(255,255,255,0.06)',
                  color: sendIdx === i ? '#F97316' : '#A8A8AE',
                  cursor: 'pointer',
                }}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {/* Send button */}
        <button
          onClick={() => {
            if (!confirm(`Отправить "${title}" для ${previewCnt ?? '?'} клиентов?`)) return
            sendMut.mutate()
          }}
          disabled={!canSend || sendMut.isPending}
          className="btn-primary disabled:opacity-40"
          style={{ marginTop: 4, marginBottom: 32 }}
        >
          {sendMut.isPending ? 'Отправляем...' : '📤 Отправить рассылку'}
        </button>
      </div>
    </Layout>
  )
}

const labelStyle: React.CSSProperties = {
  fontSize: 10, fontWeight: 500, textTransform: 'uppercase',
  letterSpacing: '0.10em', color: '#8A8A96',
}

const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 18l-6-6 6-6"/>
  </svg>
)
